var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * @export
 * Thrown by `importImageFromUrl`; `code` says why
 */
export class ImageImportError extends Error {
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
const DEFAULT_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
// The file name from the link's last path segment, with an extension that matches the type
function fileNameFor(url, type) {
    const ext = type === 'image/png' ? 'png' : type === 'image/gif' ? 'gif' : 'jpg';
    const last = decodeURIComponent(url.pathname.split('/').pop() || '').replace(/\.[^.]*$/, '').replace(/[^\w.-]+/g, '-');
    return `${last || 'image'}.${ext}`;
}
// Redraw an image of another type (webp, avif, svg…) as a JPEG
function toJpeg(blob) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const src = URL.createObjectURL(blob);
        try {
            const img = yield new Promise((resolve, reject) => {
                const el = new Image();
                el.onload = () => resolve(el);
                el.onerror = () => reject(new ImageImportError('not-image', 'The link is not an image.'));
                el.src = src;
            });
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || 1;
            canvas.height = img.naturalHeight || 1;
            (_a = canvas.getContext('2d')) === null || _a === void 0 ? void 0 : _a.drawImage(img, 0, 0);
            return yield new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new ImageImportError('not-image', 'The image could not be converted.')), 'image/jpeg', 0.9));
        }
        finally {
            URL.revokeObjectURL(src);
        }
    });
}
/**
 * @export
 * Downloads an image link in the browser and returns it as a `File`, so it can be uploaded like a
 * file the user chose (e.g. a media library, gallery or typed link picked for a profile picture).
 * Types not in `allowedTypes` are redrawn as JPEG. Throws `ImageImportError` when the site blocks
 * the download, the link fails, or it isn't an image.
 *
 * @example
 * ```ts
 * const file = await importImageFromUrl(pickedUrl);
 * await upload(file);
 * ```
 */
export function importImageFromUrl(link_1) {
    return __awaiter(this, arguments, void 0, function* (link, allowedTypes = DEFAULT_TYPES) {
        const url = new URL(link, window.location.href);
        let response;
        try {
            response = yield fetch(url.href, { credentials: url.origin === window.location.origin ? 'same-origin' : 'omit' });
        }
        catch (e) {
            throw new ImageImportError('blocked', 'This site does not allow its images to be imported.');
        }
        if (!response.ok)
            throw new ImageImportError('not-found', `The image could not be loaded (${response.status}).`);
        let blob = yield response.blob();
        if (!blob.type.startsWith('image/'))
            throw new ImageImportError('not-image', 'The link is not an image.');
        if (allowedTypes.indexOf(blob.type) < 0)
            blob = yield toJpeg(blob);
        return new File([blob], fileNameFor(url, blob.type), { type: blob.type });
    });
}
/**
 * Whether the browser may read the picture behind a link, so it can be cropped or imported.
 * Sites that send no CORS header can still be shown, but not read. Only the headers are fetched.
 */
export function canReadImageLink(link) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = new URL(link, window.location.href);
        const controller = new AbortController();
        try {
            yield fetch(url.href, { credentials: url.origin === window.location.origin ? 'same-origin' : 'omit', signal: controller.signal });
            return true;
        }
        catch (_a) {
            return false;
        }
        finally {
            controller.abort();
        }
    });
}
